<template>
  <v-container :class="RelationId ? 'pa-0' : 'list-page'" fluid>
    <div v-if="!RelationId" class="list-page-heading">
      <div>
        <div class="list-page-heading__eyebrow">Communication</div>
        <h1>Comments</h1>
        <p>
          Review internal notes and follow-up activity across parents and
          donors.
        </p>
      </div>
    </div>
    <v-row v-if="!RelationId" class="list-metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21"
              >mdi-comment-text-multiple-outline</v-icon
            >
          </div>
          <div>
            <div class="list-metric__value">{{ count }}</div>
            <div class="list-metric__label">Comments in result</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21"
              >mdi-calendar-range-outline</v-icon
            >
          </div>
          <div>
            <div class="list-metric__value">{{ dateRangeLabel }}</div>
            <div class="list-metric__label">Selected period</div>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-card :loading="loading" class="list-card comments-card" elevation="0">
      <div v-if="RelationId" class="list-card-header">
        <div>
          <div class="list-card-header__title">Comments & activity</div>
          <div class="list-card-header__subtitle">
            A running history of family follow-ups and internal updates
          </div>
        </div>
        <v-chip x-small color="primary" outlined>{{ count }}</v-chip>
      </div>
      <div v-if="!RelationId" class="list-toolbar">
        <v-text-field
          v-model="search"
          class="list-toolbar__search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search by name, user, or comment text"
          single-line
          hide-details
          clearable
        />
        <div class="list-toolbar__actions">
          <div class="comment-date-filter list-toolbar__control">
            <DateTimePicker
              defaultRange="today"
              :range="true"
              :hideTime="true"
              v-model="dateTime"
              @submit="filterByDate"
            />
          </div>
        </div>
      </div>

      <v-card-text v-if="RelationId" class="comment-composer">
        <v-layout
          class="pt-2 pl-2 pr-2"
          align-start="align-start"
          justify-center="justify-center"
          row
        >
          <v-avatar size="40px">
            <img
              v-if="currentUser.imageUrl"
              :src="currentUser.imageUrl"
              alt="User"
            />
            <img v-else src="@/assets/images/user.jpeg" alt="User" />
          </v-avatar>
          <v-layout class="ml-2 mr-2" column>
            <v-flex>
              <v-text-field
                class="mt-0 pt-0"
                v-model="comment"
                placeholder="Write an internal comment…"
                outlined
                dense
                @keydown.enter="saveComment()"
                hide-details
              >
              </v-text-field>
            </v-flex>
            <v-flex class="pt-0 text-right" v-if="comment">
              <v-btn text @click="cancel">Cancel</v-btn>
              <v-btn color="primary" depressed @click="saveComment"
                ><v-icon left size="17">mdi-send-outline</v-icon>Add
                comment</v-btn
              >
            </v-flex>
          </v-layout>
        </v-layout>
      </v-card-text>
      <v-divider class="mt-3" v-if="RelationId"></v-divider>
      <v-card-text class="comments-list pa-0">
        <v-container fluid class="pa-0">
          <v-data-table
            class="list-table comments-table"
            :headers="headers"
            :items="comments"
            :loading="loading"
            :server-items-length="count"
            :footer-props="{ 'items-per-page-options': [10, 25, 35, 50] }"
            :options.sync="options"
          >
            <template v-slot:item="{ item }">
              <tr>
                <td class="text-left comment-date">
                  <strong>{{ item.createdAt | moment("MMM D, YYYY") }}</strong
                  ><span>{{ item.createdAt | moment("h:mm A") }}</span>
                </td>
                <td class="text-left comment-author">
                  <div v-if="item.User">{{ item.User.name }}</div>
                  <div v-else>Public Entry</div>
                </td>
                <td
                  class="text-left pointer"
                  v-if="!RelationId"
                  @click="viewProfile(item)"
                >
                  <span v-if="item.TypeId === 1"
                    ><v-icon color="green">mdi-account-outline</v-icon>
                    {{ item.Customer.name }}</span
                  >
                  <span v-if="item.TypeId === 5"
                    ><v-icon color="red">mdi-hand-coin-outline</v-icon>
                    {{ item.Donor.name }}</span
                  >
                </td>
                <td class="text-left comment-message">{{ item.message }}</td>
              </tr>
            </template>

            <template v-slot:no-data>
              <div class="list-empty-state py-12">
                <v-icon size="48" color="grey lighten-1"
                  >mdi-comment-text-outline</v-icon
                >
                <strong>{{
                  search ? "No matching comments" : "No comments found"
                }}</strong>
                <span>{{
                  search
                    ? "Try a different name, user, or phrase."
                    : "Comments for this period will appear here."
                }}</span>
              </div>
            </template>
          </v-data-table>
        </v-container>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import commentService from "../service.js";
import { mapGetters } from "vuex";
const DateTimePicker = () => import("@/components/DateTimePicker");

export default {
  props: ["RelationId", "TypeId"],
  data() {
    return {
      comment: null,
      comments: [],
      loading: true,
      count: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["createdAt"],
        sortDesc: [true],
      },
      dateTime: {
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      headers: [],
      search: null,
      searchTimer: null,
    };
  },
  created() {
    this.headers.push(
      {
        text: "Date",
        align: "left",
        value: "createdAt",
        width: "20%",
        sortable: true,
      },
      {
        text: "User",
        align: "left",
        value: "User.name",
        width: "20%",
        sortable: false,
      }
    );
    if (!this.RelationId) {
      this.headers.push({
        text: "Name",
        align: "left",
        value: "name",
        width: "20%",
        sortable: false,
      });
    }
    this.headers.push({
      text: "Message",
      align: "left",
      value: "message",
      sortable: true,
    });
    this.getList();
  },
  watch: {
    RelationId: {
      handler() {
        this.getList();
      },
    },
    search: {
      handler() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          if (this.options.page !== 1) this.options.page = 1;
          else this.getList();
        }, 400);
      },
    },
    options: {
      handler() {
        this.getList();
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    dateRangeLabel() {
      const start = this.$moment(this.dateTime.start);
      const end = this.$moment(this.dateTime.end);
      if (start.isSame(end, "day")) return start.format("MMM D, YYYY");
      return `${start.format("MMM D")} – ${end.format("MMM D")}`;
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  components: {
    DateTimePicker,
  },
  methods: {
    filterByDate() {
      if (this.options.page !== 1) this.options.page = 1;
      else this.getList();
    },
    cancel() {
      this.comment = null;
    },
    save(data) {
      return commentService.create(data).then(() => {
        this.comment = null;
        this.getList();
      });
    },
    saveComment() {
      const obj = {
        RelationId: this.RelationId.id,
        message: this.comment,
        TypeId: this.TypeId,
      };

      console.log("obj", obj);
      this.save(obj);
    },
    viewProfile(val) {
      if (val.TypeId === 1) {
        this.$router.push({ path: `/parents/${val.Customer.id}/profile` });
      } else if (val.TypeId === 5) {
        this.$router.push({ path: `/donors/${val.Donor.id}/profile` });
      }
    },
    getList() {
      this.loading = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      let offset = 0;
      if (itemsPerPage > 0 && page) {
        offset = itemsPerPage * page - itemsPerPage;
      }
      let where = {
        limit: itemsPerPage,
        sortBy: sortBy[0],
        sortDesc: sortDesc[0],
      };
      if (this.RelationId) {
        where.RelationId = this.RelationId.id;
      }
      if (this.TypeId) {
        where.TypeId = this.TypeId;
      } else {
        where.filteredTypes = [1, 5];
      }
      if (!this.RelationId) {
        where.start = this.dateTime.start;
        where.end = this.dateTime.end;
      }
      if (this.search && this.search.length >= 3) {
        where.search = this.search;
      } else {
        where.offset = offset;
      }
      return commentService.getAll(where).then((response) => {
        this.comments = response.data;
        this.count = response.count;
        // console.log("comets ", this.comments);
        this.loading = false;
        return response;
      });
    },
  },
};
</script>

<style scoped>
/* Shared list page, heading, metrics, card, toolbar, search, table,
   and empty-state styles come from app.scss. */

.comments-card {
  height: 100%;
}

.comment-date-filter {
  width: 340px;
}

.comment-composer {
  padding: 16px 17px !important;
}

.comment-composer ::v-deep .v-avatar {
  background: #eaf6eb;
}

.comment-composer .v-btn {
  border-radius: 7px;
  text-transform: none;
  letter-spacing: 0;
}

.comment-date {
  display: flex;
  flex-direction: column;
}

.comment-date strong,
.comment-author {
  color: #465348;
  font-size: 11px;
}

.comment-date span {
  color: #939d94;
  font-size: 9px;
}

.comment-message {
  color: #4e5c51;
  font-size: 12px;
  line-height: 1.45;
}

@media (max-width: 599px) {
  .comment-date-filter {
    width: 100%;
  }

  .list-card-header__subtitle {
    max-width: 230px;
  }

  .comment-composer ::v-deep .v-avatar {
    display: none;
  }

  .comment-composer ::v-deep .layout.ml-2 {
    margin-left: 0 !important;
  }

  .comments-table {
    overflow-x: auto;
  }

  .comments-table ::v-deep table {
    min-width: 620px;
  }
}
</style>
